import { useCallback, useEffect, useRef } from 'react';
import ActionButtons from 'src/components/UI/ActionButtons';
import { createAbortSignal } from 'src/helper/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hook';
import { getJobDetails } from 'src/redux/job-details/api';
import { updateJobDetailsData } from 'src/redux/job-details/slice';

const WorkOrderDetails = () => {
  const dispatch = useAppDispatch();
  const workOrderData = useAppSelector((state) => state?.jobDetails?.data);

  const jobRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchJobDetails = useCallback(
    (workOrderId:string, signal?:{}) => {
      dispatch(getJobDetails(workOrderId, signal)({}))
        .unwrap()
        .then((res) => {
          if (res) {
            dispatch(updateJobDetailsData({ workOrderData: res }));
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(
            'Work Order Details Polling API Failed',
            {
              errors: error?.data?.errors || error?.message,
            },
          );
        });
    },
    [dispatch],
  );

  const handleVisibilityChange = useCallback((signal?:{}) => {
    if (document?.visibilityState === 'visible'
      && workOrderData?.work_order_id) {
      const reFetchingWorkOrderDetails = () => {
        fetchJobDetails(workOrderData?.work_order_id, signal);
        jobRef.current = setTimeout(reFetchingWorkOrderDetails, 5000);
      };
      reFetchingWorkOrderDetails();
    } else if (jobRef.current) {
      clearTimeout(jobRef.current);
    }
  }, [fetchJobDetails, workOrderData?.work_order_id]);

  useEffect(() => {
    const controller = createAbortSignal();
    handleVisibilityChange(controller?.signal);
    return () => {
      if (jobRef.current) {
        clearTimeout(jobRef.current);
      }
      controller?.abort();
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    const controller = createAbortSignal();

    const handleVisibilityChangeWithSignal = () => handleVisibilityChange(controller?.signal);

    document.addEventListener('visibilitychange', handleVisibilityChangeWithSignal);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeWithSignal);
      controller?.abort();
    };
  }, [handleVisibilityChange]);

  if (!workOrderData?.work_order_id) {
    return '';
  }

  return (

    <div className="wo-card">
      <div className="wo-card-header">
        <h5 className="card-title">
          Work Order Details
          {workOrderData?.state?.label ? <span className="btn btn-success ms-2 status-tag ">{workOrderData?.state?.label}</span> : ''}
        </h5>
      </div>
      <div className="wo-card-body">
        <ul>
          <li>
            <span>Job ID :</span>
            {' '}
            {workOrderData?.work_order_id}
          </li>
          <li>
            <span>Service Category :</span>
            {' '}
            {workOrderData?.service_category}
          </li>
          <li>
            <span>Location :</span>
            {' '}
            {workOrderData?.address}
          </li>

          <li>
            <span>Webhook URL :</span>
            {' '}
            {workOrderData?.webhook_details?.url
              ? (
                <a className="webhook-link" href={workOrderData?.webhook_details?.url}>
                  {workOrderData?.webhook_details?.url}
                </a>
              )
              : 'Not Enabled'}
          </li>

        </ul>
      </div>
      <div className="wo-card-footer">
        <h5 className="wo-card-footer-title">
          Work Order Actions
          {workOrderData?.child_job_exist ? ' (Disabled due to complex tree structure)' : ''}
        </h5>
        <div className="buttons-wrapper">
          <ActionButtons />
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetails;
